<template>
  <section>
    <b-modal
      :id="modalId"
      :ref="modalId"
      size="lg"
      title="Add/Edit Consignment"
      @shown="modalInit"
    >
      <div class="modal-body">
        <b-row>
          <b-col cols="6">
            <b-form-group label="Customer/Vendor" class="required">
              <vue-simple-suggest
                class="suggest-input form-control"
                :disabled="companyInfo.id"
                :class="{ 'is-invalid': $v.formData.companyId.$error }"
                :styles="autoCompleteStyle"
                :list="getListOfNames"
                :max-suggestions="20"
                :min-length="3"
                :debounce="200"
                :filter-by-query="true"
                value-attribute="id"
                display-attribute="name"
                v-model="chosenName"
                @select="onSuggestNameSelect"
                @input="suggestNameChange"
                @blur="$v.formData.companyId.$touch()"
                @request-done="isLoading=!isLoading"
              >
                <div slot="misc-item-below" v-if="isLoading" class="misc-item">
                  <span>Loading company...</span>
                </div>
              </vue-simple-suggest>
              <b-form-invalid-feedback>Please Select a company</b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col cols="3">
            <b-form-group label="Contact" class="required">
              <b-form-select v-model="formData.contactId" :class="{ 'is-invalid': $v.formData.contactId.$error }">
                <option v-for="(select, index) in sortSelect(contacts,'firstName')" :key="index" :value="select.id">
                  {{ select.firstName + ' ' + select.lastName }}
                </option>
              </b-form-select>
              <b-form-invalid-feedback>Please Select contact person</b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="4">
            <b-form-group label="Part #" style="grid-column: 1;" class="required">
              <b-input-group size="sm">
                <b-input-group-append v-if="(checkClaim(CAN_ADD_PARTS) || checkClaim(CAN_EDIT_PARTS))">
                  <b-button variant="light" @click="$bvModal.show('consignment-part-maintenance-model')">
                    <i v-show="formData.partId == undefined || formData.partId == null || formData.partId == 0"
                       class="part-lookup--plus fa fa-plus"></i>
                    <i v-show="formData.partId > 0" class="part-lookup--plus fa fa-pencil"></i>
                  </b-button>
                </b-input-group-append>
                <vue-simple-suggest
                  class="suggest-input form-control"
                  :styles="autoCompleteStyle"
                  :list="getList"
                  v-model="chosenPartNumber"
                  :max-suggestions="20"
                  :min-length="2"
                  :debounce="750"
                  :filter-by-query="true"
                  value-attribute="id"
                  display-attribute="partNumber"
                  @select="onSuggestSelect"
                  @input="onSuggestChange"
                  :class="{ 'is-invalid': ($v.formData.partId.$error)}"
                  @blur="onPartNumberBlur"
                  @request-done="isLoadingPart=!isLoadingPart"
                >
                  <div slot="misc-item-below" v-if="isLoadingPart" class="misc-item">
                    <span>Loading Part...</span>
                  </div>
                </vue-simple-suggest>

              </b-input-group>
              <div class="invalid-feedback">
                <b-form-invalid-feedback>Please Select a part</b-form-invalid-feedback>
              </div>
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group label="Mfr">
              <b-form-input :value="selectedPart.manufacturerName" disabled/>
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group label="NSN">
              <b-form-input :value="selectedPart.nsnNumber" disabled/>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="8">
            <b-form-group label="Description">
              <b-form-input :value="selectedPart.description" disabled/>
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group label="Class">
              <b-form-input :value="selectedPart.className" disabled/>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="4">
            <b-form-group label="Lead Free">
              <b-form-input :value="selectedPart.leadFree" disabled/>
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group label="Harmonized Code">
              <b-form-input :value="selectedPart.harmonizedCode" disabled/>
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group label="Packaging">
              <b-form-input :value="selectedPart.package" disabled/>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="4">
            <b-form-group label="Physical Package">
              <b-form-input :value="selectedPart.physPkg" disabled/>
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group label="Pieces/Package">
              <b-form-input :value="selectedPart.piecePerPkg" disabled/>
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group label="Schedule B">
              <b-form-input :value="selectedPart.scheduleB" disabled/>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="4">
            <b-form-group label="Category">
              <b-form-input :value="selectedPart.categoryName" disabled/>
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group label="Part Comments">
              <b-form-input :value="selectedPart.partComments" disabled/>
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group label="ECCN">
              <b-form-input :value="selectedPart.eccn" disabled/>
            </b-form-group>
          </b-col>
        </b-row>
        <h4 class="mt-2">Consignment Details</h4>
        <hr/>
        <b-row>
          <b-col cols="3">
            <b-form-group>
              <b-form-checkbox size="sm" v-model="formData.asPercent" inline @change="updateValues">
                Paid as Percent of Sale
              </b-form-checkbox>
            </b-form-group>
          </b-col>
          <b-col cols="2">
            <b-form-group label="Percent" :class="{ 'required': formData.asPercent }">
              <b-form-input type="number" v-model="formData.percentOfSale" :disabled="!formData.asPercent"
                            :class="{ 'is-invalid': $v.formData.percentOfSale.$error }"/>
              <b-form-invalid-feedback>Please Enter Valid Percent</b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col cols="2">
            <b-form-group label="Price/Unit" :class="{ 'required': !formData.asPercent }">
              <b-form-input type="number" v-model="formData.pricePerUnit" :disabled="formData.asPercent"
                            :class="{ 'is-invalid': $v.formData.pricePerUnit.$error }"/>
              <b-form-invalid-feedback>Please Enter Valid Price/Unit</b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="8">
            <b-form-group label="Comments">
              <b-form-textarea v-model="formData.comments"/>
            </b-form-group>
          </b-col>
        </b-row>
      </div>
      <template v-slot:modal-footer>
        <b-button size="sm" variant="secondary" @click="$bvModal.hide(modalId)">
          Cancel
        </b-button>
        <b-button size="sm" variant="success" @click="saveConsignements(1,$event)" :disabled="isSaving">
          <i v-if="isSaving" class="fa fa-spinner fa-pulse fa-fw mr-1"></i>Save
        </b-button>
        <b-button size="sm" variant="primary" @click="saveConsignements(2,$event)" :disabled="isSaving">
          <i v-if="isSaving" class="fa fa-spinner fa-pulse fa-fw mr-1"></i>Save & Close
        </b-button>
        <b-button size="sm" variant="warning" @click="saveConsignements(3,$event)" :disabled="isSaving">
          <i v-if="isSaving" class="fa fa-spinner fa-pulse fa-fw mr-1"></i>Save & Receive
        </b-button>
      </template>
    </b-modal>
    <b-modal id="consignment-part-maintenance-model" size="xl" ok-title="Save" scrollable hide-footer>
      <part-form propForm="consignment" :partId="formData.partId" :partNumberProp="chosenPartNumber" @cancel="onPartModelClose"
                 @partCreated="onPartCreated" @partUpdated="onPartUpdated"/>
    </b-modal>
    <b-modal id="receive-consignment-modal" ref="receive-consignment-modal" size="xl" hide-footer title="Add Consigned Inventory">
      <consignment-receive ref="receiveConsignment" :consignmentId="formData.id" :consignmentObj="formData" @onRedirectToList="$bvModal.hide('receive-consignment-modal')" />
    </b-modal>
  </section>
</template>
<script>
  import { mapGetters, mapActions, mapMutations } from 'vuex';
  import VueSimpleSuggest from 'vue-simple-suggest';
  import { required, requiredIf, between, integer } from 'vuelidate/lib/validators';
  import { numberGreaterThanOrEqual } from '@/shared/validators';
  import { AUTO_COMPLETE_STYLES } from '@/shared/constants';
  import PartForm from '@/pages/Parts/components/PartForm';
  import ConsignmentReceive from '../ReceiveForm';

  export default {
    components: {
      VueSimpleSuggest,
      PartForm,
      ConsignmentReceive
    },
    props: {
      companyInfo: {
        type: Object,
        default: () => {
        },
      },
      partDetails: {
        type: Object,
        default: () => {
        },
      },
      editDetails: {
        type: Object,
        default: () => {},
      },
      modalId: {
        type: String,
        default: 'add-consignment-modal',
      },
    },
    data() {
      return {
        chosenName: '',
        chosenPartNumber: (this.partDetails) ? this.partDetails.partNumber : '',
        autoCompleteStyle: AUTO_COMPLETE_STYLES,
        isLoading: false,
        isSaving: false,
        isLoadingPart: false,
        selectedPart: (this.partDetails) ? this.partDetails : {},
        formData: {
          id: 0,
          companyId: '',
          contactId: '',
          partId: '',
          asPercent: true,
          percentOfSale: 0,
          pricePerUnit: 0,
          active: true,
          comments: '',
        },
      };
    },
    validations: {
      formData: {
        companyId: { required },
        contactId: { required },
        partId: { required },
        percentOfSale: {
          required: requiredIf(function() {
            return this.formData.asPercent;
          }),
          //integer,
          between: between(0, 100),
        },
        pricePerUnit: {
          required: requiredIf(function() {
            return !this.formData.asPercent;
          }),
          numberGreaterThanOrEqual: numberGreaterThanOrEqual(0),
        },
      },
    },
    computed: {
      ...mapGetters('quoteAdd', ['contacts']),
      ...mapGetters('requirementAdd', ['partNumbers','currentPart']),
    },
    methods: {
      ...mapActions('companies', ['getByNameAndAccountType']),
      // ...mapActions('quoteAdd', ['getContacts', 'clearContacts']),
      ...mapActions('quoteAdd', ['getContacts']),
      ...mapMutations('quoteAdd', {quoteAddSetItem: 'setItem'}),
      ...mapActions('requirementAdd', ['getPartById', 'getPartNumbers']),
      ...mapActions('consignments', ['manage']),
      updateValues(newVal) {
        if (newVal) {
          this.formData.pricePerUnit = 0;
        } else {
          this.formData.percentOfSale = 0;
        }
      },

      /* Set data into forms when open from part list page, edit consignment page and company detail page*/
      async modalInit(){
        if(this.editDetails && this.editDetails.id){
          //console.log('this.',this.editDetails);
          this.formData = {
            id: this.editDetails.id,
            companyId: this.editDetails.companyId,
            contactId: this.editDetails.contactId,
            partId: this.editDetails.partId,
            asPercent: this.editDetails.asPercent,
            percentOfSale: this.editDetails.percentOfSale,
            pricePerUnit: this.editDetails.pricePerUnit,
            active: true,
            comments: this.editDetails.comments,
          }
          this.selectedPart = {
            categoryName: this.editDetails.categoryName,
            className: this.editDetails.className,
            partsComments: this.editDetails.partsComments,
            description: this.editDetails.description,
            eccn: this.editDetails.eccn,
            harmonizedCode: this.editDetails.harmonizedCode,
            inventoryCategoryId: this.editDetails.inventoryCategoryId,
            inventoryClassId: this.editDetails.inventoryClassId,
            leadFree: this.editDetails.leadFree,
            manufacturerId: this.editDetails.manufacturerId,
            mfgName: this.editDetails.mfgName,
            name: this.editDetails.name,
            nsnNumber: this.editDetails.nsnNumber,
            package: this.editDetails.package,
            packageId: this.editDetails.packageId,
            percentOfSale: this.editDetails.percentOfSale,
            physPkg: this.editDetails.physPkg,
            piecePerPkg: this.editDetails.piecePerPkg,
            pricePerUnit: this.editDetails.pricePerUnit,
            scheduleB: this.editDetails.scheduleB,
            uoMId: this.editDetails.uoMId,
            uom: this.editDetails.uom
          };
          this.chosenPartNumber = this.editDetails.partNumber;
          this.chosenName = this.editDetails.name;
          await this.getContacts(this.editDetails.companyId);
        } else if(this.partDetails && this.partDetails.id){
          this.formData = {
            id: 0,
            companyId: '',
            contactId: '',
            partId: this.partDetails.id,
            asPercent: true,
            percentOfSale: 0,
            pricePerUnit: 0,
            active: true,
            comments: '',
          }
          this.selectedPart = Object.assign({},this.partDetails);
          this.chosenPartNumber = this.partDetails.partNumber;
        }
      },

      /*Save Consignements data*/
      async saveConsignements(type, e) {
        e.preventDefault();
        this.$v.formData.$touch();
        if (this.$v.formData.$invalid) {
          return;
        }
        try {
          this.isSaving = true;
          this.formData = await this.manage(this.formData);
          this.isSaving = false;
          this.$emit('redreshGrid');
          if (type == 2) {
            this.$v.formData.$reset();
            this.$bvModal.hide(this.modalId);
          } else if (type == 3) {
            //redirect to some page
            this.$bvModal.hide(this.modalId);
            this.$bvModal.show('receive-consignment-modal');
          }
        }catch (e) {
          this.isSaving = false;
          /*console.log(e.message);*/
        }
      },

      /* Company select box functions */
      async onSuggestNameSelect(suggestion) {
        this.formData.companyId = suggestion.id;
        await this.getContacts(suggestion.id);
      },
      async suggestNameChange(val) {
        this.isLoading = true;
        if (val === '' || val !== this.chosenName) {
          this.formData.companyId = '';
          // this.clearContacts();
          this.quoteAddSetItem({ resource: 'contacts', payload: [] });
        }
      },
      async getListOfNames(name) {
        return await this.getByNameAndAccountType({ typeId: 0, name });
      },
      /* END Company select box functions */

      /* Part # select box functions */
      async getList(partNumber) {
        await this.getPartNumbers(partNumber);
        return this.partNumbers;
      },
      async onSuggestSelect(suggestion) {
        //console.log('Part Number Selected', suggestion);
        this.formData.partId = suggestion.id;
        console.log(suggestion, "Suggestion")
        this.selectedPart = {
          ...suggestion,
          manufacturerName: suggestion.mfgName,
          partComments: suggestion.comments,
          comments: null,
        };
      },
      async onSuggestChange() {
        this.isLoadingPart = true;
        if (this.selectedPart.partNumber && this.chosenPartNumber !== this.selectedPart.partNumber) {
          this.formData.partId = '';
        }
      },
      async onPartNumberBlur() {
        this.$v.formData.partId.$touch();
        if (this.chosenPartNumber && !this.formData.partId) {
          const deleteConfirmed = await this.openConfirmBox({message: `${this.chosenPartNumber} is not in the database. Press Continue to add them.`,
            okTitle: 'Continue',
          });
          if (deleteConfirmed) {
            // this.$refs.partMaintenanceModel.populatePartNumber(this.chosenPartNumber);
            this.$bvModal.show('part-maintenance-model');
          } else {
            //this.chosenPartNumber = "";
          }
        }
      },
      /* END Part # select box functions */

      /* New Part add edit functions */
      async onPartModelClose() {
        this.$bvModal.hide('part-maintenance-model');
      },
      async onPartCreated(e) {
        //console.log('onPartCreated: ', e);
        this.$bvModal.hide('part-maintenance-model');
        this.chosenPartNumber = e.partNumber;
        this.formData.partId = e.id;
        this.selectedPart = {
          ...e,
          partComments: e.comments,
          comments: null,
        };

      },
      async onPartUpdated(e) {
        //console.log('onPartUpdated: ' , e);
        this.$bvModal.hide('part-maintenance-model');
        this.chosenPartNumber = e.partNumber;
        this.selectedPart = {
          ...e,
          partComments: e.comments,
          comments: null,
        };
      },
      /* END New Part add edit functions */
    },
    watch: {
      companyInfo(newData){
        //console.log('this.newData',newData);
        if(!newData){
          newData = {
            id: '',
            name: ''
          }
        }else{
          this.getContacts(newData.id);
        }
        this.formData.companyId = newData.id;
        this.chosenName = newData.name;
        this.selectedPart = {}
        this.chosenPartNumber = "";
        this.formData = {
          id: 0,
          companyId: newData.id,
          contactId: '',
          partId: '',
          asPercent: true,
          percentOfSale: 0,
          pricePerUnit: 0,
          active: true,
          comments: '',
        }
      }
    },
  };
</script>


<style>
#receive-consignement-modal .modal-dialog {
  max-width: 1750px !important;
}

</style>